// React
import { useMemo, Fragment } from "react"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Table
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
	CellContext,
} from "@/lib/table"

// UI
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"

// Translations
import { useLang } from "@/context/useLang"
import { useTrans } from "@/lib/i18n"

// Types
import type { PublicProjectType } from "@/api/graphql/types"

/**
 * ProjectsNestedTable
 * Nested table component that displays projects for a specific project owner
 */
export function ProjectsNestedTable({
	projects,
}: {
	projects: Array<PublicProjectType>
}) {
	const DateTime = useLocalizedDateTime()
	const t = useTrans(["common", "dashboard"])
	const { formatNumber, formatCurrency } = useLang()

	const columnHelper = createColumnHelper<PublicProjectType>()

	const columns = useMemo(
		() => [
			columnHelper.accessor("name", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:stats.projects_table.header.project")}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, string | null>) => (
					<TableDataCell className="font-medium">
						{info.getValue()}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("start", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:stats.projects_table.header.start_date")}
					</TableHeading>
				),
				cell: (info: CellContext<PublicProjectType, string | null>) => {
					const startDate = info.getValue()
					return (
						<TableDataCell>
							{startDate && typeof startDate === "string" ? (
								DateTime.fromISO(startDate).toLocaleString()
							) : (
								<span className="text-gray-400">-</span>
							)}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor(
				(row) => {
					if (row?.supplier !== null && row?.supplier !== undefined) {
						return (
							row.supplier as {
								id?: string
								name?: string | null
							}
						)?.name
					}
					return null
				},
				{
					id: "supplier",
					header: () => (
						<TableHeading variant="static">
							{t("dashboard:statistics.platform.supplier")}
						</TableHeading>
					),
					cell: (
						info: CellContext<
							PublicProjectType,
							string | null | undefined
						>,
					) => {
						const supplierName = info.getValue()
						return (
							<TableDataCell>
								{supplierName ?? (
									<span className="text-gray-400">-</span>
								)}
							</TableDataCell>
						)
					},
				},
			),
			columnHelper.accessor("total_amount", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard:statistics.platform.amount_raised")}
					</TableHeading>
				),
				cell: (info) => {
					return (
						<TableDataCell>
							{info.getValue() ? (
								<>
									{formatCurrency(
										Number(info.getValue()) ?? 0,
									)}
								</>
							) : (
								<span className="text-gray-400">-</span>
							)}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor("payment_arrears", {
				header: () => (
					<TableHeading variant="static">
						{t(
							"dashboard:stats.projects_table.header.payment_arrears",
						)}
					</TableHeading>
				),
				cell: (info) => {
					return (
						<TableDataCell>
							{info.getValue()
								? t(`common:project.payment_arrears.true`)
								: t(`common:project.payment_arrears.false`)}
						</TableDataCell>
					)
				},
			}),
		],
		[columnHelper, t, formatNumber, DateTime],
	)

	const table = useReactTable({
		columns,
		data: projects,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<div className="bg-gray-50 p-4">
			<Table>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id} withHover={false}>
							{headerGroup.headers.map((header) => (
								<Fragment key={header.id}>
									{flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
								</Fragment>
							))}
						</TableRowCell>
					))}
				</TableHead>
				<TableBody>
					{table.getRowModel().rows.map((row, index) => (
						<TableRowCell key={row.id} isOdd={index % 2 === 0}>
							{row.getVisibleCells().map((cell) => (
								<Fragment key={cell.id}>
									{flexRender(
										cell.column.columnDef.cell,
										cell.getContext(),
									)}
								</Fragment>
							))}
						</TableRowCell>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
